import React, { useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

const TYPE_META = {
  service: {
    label: "Service",
    badge: "bg-blue-100 text-blue-800 border-blue-300",
  },
  inspection: {
    label: "Inspection",
    badge: "bg-yellow-100 text-yellow-800 border-yellow-300",
  },
  wip: {
    label: "WIP",
    badge: "bg-purple-100 text-purple-800 border-purple-300",
  },
  operations: {
    label: "Operations",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
  },
};

function formatTime(t) {
  if (!t) return "—";

  const [hh, mm = "00"] = String(t).split(":");
  const h = parseInt(hh || "0", 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;

  return `${h12}:${mm.padStart(2, "0")} ${ampm}`;
}

function formatDate(ymd) {
  if (!ymd) return "—";

  const match = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return ymd;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function TechHistoryDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const { type, id } = useParams();
  const [searchParams] = useSearchParams();

  const selectedDate = searchParams.get("date") || "";

  const storageKey =
    `techHistoryDetails:${type}:${id}:${selectedDate}`;

  const stored = useMemo(() => {
    try {
      const value = sessionStorage.getItem(storageKey);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error("Unable to restore history details", error);
      return null;
    }
  }, [storageKey]);

  const item =
    location.state?.item ||
    stored?.item ||
    null;

  if (!item) {
    return (
      <div className="mx-auto max-w-[1200px] p-4">
        <div className="rounded-xl border bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold">
            History Details
          </h1>

          <p className="mt-3 text-gray-500">
            This history record is no longer available.
          </p>

          <button
            onClick={() => navigate(-1)}
            className="mt-5 rounded-lg bg-gray-900 px-4 py-2 text-white"
          >
            Back to History
          </button>
        </div>
      </div>
    );
  }

  const typeMeta =
    TYPE_META[item._type] || {
      label: item._type,
      badge: "bg-gray-100 text-gray-700 border-gray-300",
    };

  const isCanceled =
    (item.status || "").toLowerCase().includes("cancel");

  const isComplete =
    (item.status || "").toLowerCase().includes("complete");

  const leadTech =
    item.lead_tech ||
    item.tech_name ||
    item.serv_tech_name ||
    item.meta?.tech_name ||
    "";

  const additionalTechs =
    Array.isArray(item.additionalTechNames)
      ? item.additionalTechNames
      : [];

  const description =
    item._type === "service"
      ? item.TROUBLE
      : item._type === "wip"
      ? item.notes
      : item._type === "operations"
      ? item.notes
      : "";

  const internalNotes =
    item._type === "wip" ||
    item._type === "operations"
      ? item.meta?.coordinator_comment
      : item.notes;

  const techNotes = item.meta?.tech_notes || "";

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="mx-auto max-w-[1200px] px-4 py-6">

        {/* BACK */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 text-sm font-semibold text-gray-600 hover:text-gray-900"
        >
          ← Back to History
        </button>

        {/* HEADER */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">

            <div>

              <div className="mb-2 flex flex-wrap gap-2">

                <span
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${typeMeta.badge}`}
                >
                  {typeMeta.label}
                </span>

                <span
                  className={[
                    "rounded-full px-3 py-1 text-xs font-semibold",
                    isComplete
                      ? "bg-green-100 text-green-700"
                      : isCanceled
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-700",
                  ].join(" ")}
                >
                  {item.status || "No Status"}
                </span>

              </div>

              <h1 className="text-2xl font-bold text-gray-900">
                {item.job_site || "No Address"}
              </h1>

              {item.customer && (
                <p className="mt-1 text-lg text-gray-500">
                  {item.customer}
                </p>
              )}

              <p className="mt-2 text-xs text-gray-400">
                Record ID: {item.id}
              </p>

            </div>

          </div>

        </section>

        {/* BODY */}
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">

          {/* LEFT / MAIN */}
          <div className="space-y-5 lg:col-span-2">

            {/* DESCRIPTION */}
            {description && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {item._type === "service"
                    ? "PO# / Service Ticket Notes"
                    : item._type === "wip"
                    ? "Job Description"
                    : "Description"}
                </h2>

                <div
                  className={
                    item._type === "service"
                      ? "whitespace-pre-wrap font-medium text-red-700"
                      : "whitespace-pre-wrap text-gray-900"
                  }
                >
                  {description}
                </div>

              </section>
            )}

            {/* TECH NOTES */}
            {techNotes && (
              <section className="rounded-xl border border-blue-200 bg-white p-5 shadow-sm">

                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tech Notes
                </h2>

                <ExpandableText
                  text={techNotes}
                  className="bg-blue-50 border-blue-200"
                />

              </section>
            )}

            {/* INTERNAL NOTES */}
            {internalNotes && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Internal Notes
                </h2>

                <ExpandableText
                  text={internalNotes}
                  className="bg-gray-50 border-gray-200"
                />

              </section>
            )}

            {/* RELATED LINKS */}
            {Array.isArray(item.links) &&
              item.links.length > 0 && (
                <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Related Links
                  </h2>

                  <div className="space-y-3">

                    {item.links.map((link) => (
                      <div
                        key={link.id}
                        className="rounded-lg border border-gray-200 p-3"
                      >
                        <div className="text-sm font-semibold">
                          {link.label || "Untitled"}
                        </div>

                        <a
                          href={
                            /^https?:\/\//i.test(link.link)
                              ? link.link
                              : `https://${link.link}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 block break-all text-sm text-indigo-600 hover:underline"
                        >
                          {link.link}
                        </a>
                      </div>
                    ))}

                  </div>

                </section>
              )}

          </div>

          {/* RIGHT */}
          <div className="space-y-5">

            {/* DATE / TIME */}
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

              <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Schedule
              </h2>

              <div className="space-y-4">

                <div>
                  <div className="text-xs text-gray-400">
                    Date
                  </div>

                  <div className="font-semibold">
                    {formatDate(item._date)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-400">
                    Time
                  </div>

                  <div className="font-semibold">
                    {formatTime(item.start_time)}

                    {item.end_time
                      ? ` – ${formatTime(item.end_time)}`
                      : ""}
                  </div>
                </div>

                {item.meta?.schedule_from &&
                  item.meta?.schedule_to && (
                    <div>
                      <div className="text-xs text-gray-400">
                        Scheduled Range
                      </div>

                      <div className="font-medium">
                        {formatDate(item.meta.schedule_from)}
                      </div>

                      <div className="text-xs text-gray-400">
                        through
                      </div>

                      <div className="font-medium">
                        {formatDate(item.meta.schedule_to)}
                      </div>
                    </div>
                  )}

              </div>

            </section>

            {/* TECHNICIANS */}
            {(leadTech || additionalTechs.length > 0) && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Technicians
                </h2>

                {leadTech && (
                  <div>
                    <div className="text-xs text-gray-400">
                      Lead Technician
                    </div>

                    <div className="font-semibold">
                      {leadTech}
                    </div>
                  </div>
                )}

                {additionalTechs.length > 0 && (
                  <div className="mt-4">

                    <div className="mb-1 text-xs text-gray-400">
                      Additional Technician(s)
                    </div>

                    {additionalTechs.map((tech, i) => (
                      <div
                        key={`${tech}-${i}`}
                        className="font-medium text-blue-700"
                      >
                        {tech}
                      </div>
                    ))}

                  </div>
                )}

              </section>
            )}

            {/* LOGGED BY */}
            {item.logged_by && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Logged By
                </h2>

                <div className="font-semibold text-gray-900">
                  {item.logged_by}
                </div>

              </section>
            )}

            {/* SERVICE CONTACT */}
            {item._type === "service" && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Contact
                </h2>

                <div className="space-y-3">

                  <div>
                    <div className="text-xs text-gray-400">
                      Called By
                    </div>

                    <div className="font-medium">
                      {item.meta?.called_by || "—"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400">
                      Phone
                    </div>

                    {item.meta?.phone ? (
                      <a
                        href={`tel:${item.meta.phone}`}
                        className="font-medium text-blue-600"
                      >
                        {item.meta.phone}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>

                </div>

              </section>
            )}

            {/* INSPECTION CONTACT */}
            {item._type === "inspection" && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Contact
                </h2>

                <div className="space-y-3">

                  {item.meta?.contact_name && (
                    <div>
                      <div className="text-xs text-gray-400">
                        Name
                      </div>

                      <div className="font-medium">
                        {item.meta.contact_name}
                      </div>
                    </div>
                  )}

                  {item.meta?.contact_phone && (
                    <div>
                      <div className="text-xs text-gray-400">
                        Phone
                      </div>

                      <a
                        href={`tel:${item.meta.contact_phone}`}
                        className="font-medium text-blue-600"
                      >
                        {item.meta.contact_phone}
                      </a>
                    </div>
                  )}

                  {item.meta?.contact_email && (
                    <div>
                      <div className="text-xs text-gray-400">
                        Email
                      </div>

                      <a
                        href={`mailto:${item.meta.contact_email}`}
                        className="break-all font-medium text-blue-600"
                      >
                        {item.meta.contact_email}
                      </a>
                    </div>
                  )}

                </div>

              </section>
            )}

            {/* WIP */}
            {item._type === "wip" && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Job Information
                </h2>

                <div className="space-y-3">

                  <div>
                    <div className="text-xs text-gray-400">
                      Job #
                    </div>

                    <div className="font-bold text-green-800">
                      {item.meta?.job_number || "—"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400">
                      Type
                    </div>

                    <div className="font-medium">
                      {item.meta?.type || "—"}
                    </div>
                  </div>

                </div>

              </section>
            )}

            {/* TIME LOG */}
            {item.meta?.time_log && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Time Log
                </h2>

                <div className="whitespace-pre-wrap text-sm text-gray-900">
                  {item.meta.time_log}
                </div>

              </section>
            )}

          </div>

        </div>

      </div>
    </div>
  );
}

function ExpandableText({ text, className = "" }) {
  const [expanded, setExpanded] = useState(false);

  const content = String(text || "");
  const needsTruncate = content.length > 400;

  const visible =
    needsTruncate && !expanded
      ? `${content.slice(0, 400)}…`
      : content;

  return (
    <div>

      <div
        className={`rounded-lg border p-3 whitespace-pre-wrap text-gray-900 ${className}`}
      >
        {visible}
      </div>

      {needsTruncate && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 rounded bg-gray-100 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-gray-200"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}

    </div>
  );
}